/**
 * Annotation Helper Utilities
 * 
 * Functions for grouping, sorting, and processing claim annotations.
 */

import type { ClaimAnnotation, ClaimLine, FieldAnnotations, GroupedAnnotations } from '../types';

import { getFieldLabel, mapToFormField, parseFieldKey } from './fieldMapping';
import { getFieldBox } from './fieldNameMapping';

/**
 * Group annotations by field and severity
 */
export function groupAnnotations(
  annotations: ClaimAnnotation[],
  claimLines?: ClaimLine[]
): GroupedAnnotations {
  // Group by field first
  const fieldMap = new Map<string, ClaimAnnotation[]>();
  
  annotations.forEach(annotation => {
    const fieldKey = annotation.field || 'general';
    const existing = fieldMap.get(fieldKey) || [];
    existing.push(annotation);
    fieldMap.set(fieldKey, existing);
  });
  
  // Convert to FieldAnnotations array
  const fieldAnnotations: FieldAnnotations[] = Array.from(fieldMap.entries()).map(
    ([fieldKey, annotations]) => {
      // Find highest severity
      let highestSeverity: 'error' | 'warning' | 'info' = 'info';
      annotations.forEach(ann => {
        if (ann.severity === 'error') highestSeverity = 'error';
        else if (ann.severity === 'warning' && highestSeverity !== 'error') {
          highestSeverity = 'warning';
        }
      });
      
      // Get CMS box from annotation or fallback to field mapping
      let cmsBox = annotations[0]?.cms_box;
      if (!cmsBox) {
        // Try to get box from field mapping
        const boxNumber = getFieldBox(fieldKey);
        if (boxNumber) {
          cmsBox = boxNumber;
        }
      }
      
      return {
        fieldKey,
        parsedField: parseFieldKey(fieldKey),
        annotations,
        formFieldPath: mapToFormField(fieldKey, claimLines) || undefined,
        highestSeverity,
        cmsBox,
      };
    }
  );
  
  // Sort field annotations by CMS box number if available
  fieldAnnotations.sort((a, b) => {
    const boxA = extractBoxNumber(a.cmsBox);
    const boxB = extractBoxNumber(b.cmsBox);
    return boxA - boxB;
  });
  
  // Group by severity
  const errors = fieldAnnotations.filter(f => f.highestSeverity === 'error');
  const warnings = fieldAnnotations.filter(f => f.highestSeverity === 'warning');
  const info = fieldAnnotations.filter(f => f.highestSeverity === 'info');
  
  // Count individual annotations (not fields)
  const errorCount = errors.reduce((sum, f) => sum + f.annotations.filter(a => a.severity === 'error').length, 0);
  const warningCount = warnings.reduce((sum, f) => sum + f.annotations.filter(a => a.severity === 'warning').length, 0);
  const infoCount = info.reduce((sum, f) => sum + f.annotations.filter(a => a.severity === 'info').length, 0);
  
  return {
    errors,
    warnings,
    info,
    errorCount,
    warningCount,
    infoCount,
    totalCount: errorCount + warningCount + infoCount,
  };
}

/**
 * Extract box number from CMS box reference
 * @example
 * extractBoxNumber("Box 3") // 3
 * extractBoxNumber("Box 24D") // 24
 */
function extractBoxNumber(boxRef?: string | null): number {
  if (!boxRef) return 999; // Put items without box numbers at the end
  
  const match = boxRef.match(/Box\s+(\d+)/i);
  if (match) {
    return parseInt(match[1], 10);
  }
  
  // Try to extract just numbers
  const numbers = boxRef.match(/\d+/);
  if (numbers) {
    return parseInt(numbers[0], 10);
  }
  
  return 999;
}

/**
 * Get a display title for a field annotation group
 */
export function getFieldDisplayTitle(fieldAnnotation: FieldAnnotations): string {
  // Use the field label utility
  const label = getFieldLabel(fieldAnnotation.fieldKey);
  
  // Add CMS box reference if available
  const boxRef = fieldAnnotation.cmsBox;
  if (boxRef) {
    return `${label} (Box ${boxRef})`;
  }
  
  return label;
}

/**
 * Get the severity color for MUI components
 */
export function getSeverityColor(severity: 'error' | 'warning' | 'info'): 'error' | 'warning' | 'info' | 'success' {
  switch (severity) {
    case 'error':
      return 'error';
    case 'warning':
      return 'warning';
    case 'info':
      return 'info';
    default:
      return 'info';
  }
}

/**
 * Check if annotations have any errors
 */
export function hasErrors(annotations: ClaimAnnotation[]): boolean {
  return annotations.some(a => a.severity === 'error');
}

/**
 * Check if claim can be submitted based on annotations
 */
export function canSubmitClaim(annotations: ClaimAnnotation[]): boolean {
  // Can't submit if there are any errors
  return !hasErrors(annotations);
}

/**
 * Format annotation message with additional context
 */
export function formatAnnotationMessage(annotation: ClaimAnnotation): string {
  let message = annotation.message;
  
  // Add rule name if available and not already in message
  if (annotation.rule_name && !message.includes(annotation.rule_name)) {
    message = `${annotation.rule_name}: ${message}`;
  }
  
  return message;
}

/**
 * Get unique field keys from annotations
 */
export function getUniqueFieldKeys(annotations: ClaimAnnotation[]): string[] {
  const fieldSet = new Set<string>();
  annotations.forEach(ann => {
    if (ann.field) {
      fieldSet.add(ann.field);
    }
  });
  return Array.from(fieldSet);
}